'use client';

import React, { useState } from 'react';

import { Typography, Box } from '@mui/material';
import styled from 'styled-components';

import HeadingsList from '../../article/[slug]/HeadingsList';
import Button from '@/ui/components/Button';
import ProductBadge from '@/ui/components/ContentHeader/ProductBadge';
import ParsedContent from '@/ui/components/ParsedContent';
import TwoColumnLayout from '@/ui/containers/layout/TwoColumn';
import { formatDateForLocale } from '@/utils/helpers';
import { endpoints } from '@/utils/queryData/rest/endpoints';
import { slugify } from '@/utils/slugify';

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.31rem;
`;

const Version = styled.div`
  &:not(:last-child) {
    &:after {
      color: ${props => props.theme.palette.gray.main};
      content: '⬢';
      display: block;
      font-size: 16px;
      line-height: 16px;
      margin-bottom: 40px;
      margin-top: 40px;
      text-align: center;
    }
  }
`;

const LoadAll = styled.div`
  margin-top: 40px;
  text-align: center;
`;

interface ContentProps {
  loadAll: boolean;
  versions: DownloadItem[];
  locale: string;
  slug: string;
}

export default function Content({ loadAll: initialLoadAll, versions: initialVersions, locale, slug }: ContentProps) {
  const [loadAll, setLoadAll] = useState(initialLoadAll);
  const [versions, setVersions] = useState(initialVersions);
  const [loading, setLoading] = useState(false);

  const handleLoadAll = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${endpoints.downloads}?locale=${locale}&meta_key=type&meta_value=firmware&product=${slug}&orderby=date&order=DESC`
      );
      const data = await response.json();
      setVersions(data.data);
      setLoadAll(false);
    } catch (error) {
      console.error('Failed to fetch all versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const headingsArray = versions.map(item => ({
    hashAnchor: `version-${slugify(item.title)}`,
    title: `Version ${item.title}`,
    type: 'h2',
  }));

  return (
    <TwoColumnLayout>
      <main>
        <div>
          {versions.map(version => (
            <Version
              key={version.id}
              id={`version-${slugify(version.title)}`}
              data-visible-id={`version-${slugify(version.title)}`}
            >
              <Typography variant="h3" component="h2">
                Version {version.title}
              </Typography>

              <Box mt={2}>
                <Badges>
                  <ProductBadge
                    product={{
                      title: formatDateForLocale('cs', version.meta.release_date),
                    }}
                  />
                </Badges>
              </Box>

              <Box mt={3}>
                <article>
                  <ParsedContent content={version.content} />
                </article>
              </Box>
            </Version>
          ))}
        </div>

        {loadAll && (
          <LoadAll>
            <Button
              label="Show all versions"
              variant="outlined"
              onClick={handleLoadAll}
              disabled={loading}
              loading={loading}
            />
          </LoadAll>
        )}
      </main>
      <HeadingsList headingsArray={headingsArray} />
    </TwoColumnLayout>
  );
}
